import React, { useState, useCallback, useRef, useEffect } from "react";
import Cropper, { Area, Point } from "react-easy-crop";
import { useDispatch } from "react-redux";
import { addModal } from "../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../const/ModalKeys";
import { useSelector } from "react-redux";
import { RootState } from "../../libraries/state_reducers/store";
import {
  provideFileEdited,
  provideFileString,
} from "../../libraries/state_reducers/service/ImageEditorReducer";
import UtilityHelper from "../utilities/UtilityHelper";
import { ResponsiveDeviceType } from "../../widget/layouts/ScreenMedia";
import { ButtonSingle } from "../../widget/common/form/Button";
import AppTranslation from "../app_translation/AppTranslation";
import { imageExtensions, videoExtensions } from "../../const/FileExtensions";
import ShowToast from "../../widget/common/ui/Toast";

type ImageEditorPickType = {
  children: React.ReactNode;
  fromAction: string;
  callback: (file: File | null, durationSeconds: number) => void;
  onClick: () => void;
};

export const ImageEditorPick: React.FC<ImageEditorPickType> = ({
  children,
  fromAction = "",
  callback,
  onClick,
}) => {
  const dispatch = useDispatch();
  const type: number = ResponsiveDeviceType();

  const fileEditedDetailSelector = useSelector(
    (state: RootState) => state.imageEditor.fileEditedDetail
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.type.split("/")[1].toLowerCase();

    if (imageExtensions.includes(ext) || videoExtensions.includes(ext)) {
      onClick();
      if (file) {
        //setImage(URL.createObjectURL(e.target.files[0]));
        dispatch(
          provideFileString({
            fileString: URL.createObjectURL(file),
            fromAction: fromAction,
          })
        );

        if (imageExtensions.includes(ext)) {
          dispatch(
            addModal({
              id: ModalKeys.image_editor,
              size: "lg",
              screenMobileView: type === 3 ? false : true,
            })
          );
        } else if (videoExtensions.includes(ext)) {
          dispatch(
            addModal({
              id: ModalKeys.video_editor,
              size: "lg",
              screenMobileView: type === 3 ? false : true,
            })
          );
        }

        if (inputRef.current) {
          inputRef.current.value = "";
        }
      }
    } else {
      ShowToast.error(AppTranslation.text("invalid_file_type"));
    }
  };

  /*  useEffect(() => {
    inputRef.current?.click();
  }, []); */

  useEffect(() => {
    if (fileEditedDetailSelector != null && typeof callback === "function") {
      //console.log(fileEditedDetailSelector.fileString);

      if (fileEditedDetailSelector.type === "image") {
        callback(
          UtilityHelper.base64ToFile(
            fileEditedDetailSelector.fileString!,
            "image.png"
          ),
          0
        );
      } else if (fileEditedDetailSelector.type === "video") {
        // WAS DIRECT IN VIDEO TRIMMER
        /* console.log(
          UtilityHelper.base64ToFile(
            fileEditedDetailSelector.fileString!,
            "video.mp4"
          )
        ); */
        callback(
          UtilityHelper.base64ToFile(
            fileEditedDetailSelector.fileString!,
            "video.mp4"
          ),
          fileEditedDetailSelector.durationSeconds ?? 0
        );
      }
    }

    return () => {
      dispatch(provideFileEdited(null));
    };
    // eslint-disable-next-line
  }, [fileEditedDetailSelector]);

  return (
    <div className="file-editor-picker">
      <input
        ref={inputRef}
        type="file"
        //accept="image/*"
        accept="image/*,video/*"
        onChange={handleFile}
      />
      {children}
    </div>
  );
};

interface Props {
  imageSrc: string;
  onComplete: (croppedImage: string) => void;
}

export const ImageCropper: React.FC<Props> = ({ imageSrc, onComplete }) => {
  const type: number = ResponsiveDeviceType();

  const appplyActionStatus = useSelector(
    (state: RootState) => state.imageEditor.appplyActionStatus
  );

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const getCroppedImage = useCallback(async () => {
    console.log(`getCroppedImage`);
    if (!croppedAreaPixels) return;

    const cropped = await cropImage(imageSrc, croppedAreaPixels);
    onComplete(cropped);
  }, [croppedAreaPixels, imageSrc, onComplete]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value);
    setRotation(newValue);
  };

  useEffect(() => {
    if (appplyActionStatus) {
      getCroppedImage();
    }
    // eslint-disable-next-line
  }, [appplyActionStatus]);

  return (
    <>
      <div className="overflow-hidden">
        <div
          style={{
            position: "relative",
            width: "100%",
            height: type === 3 ? "50dvh" : "83dvh",
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="image-editor-footer">
          <div className="editor-option px-4 py-2 d-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="25"
              fill="red"
              className="bi bi-arrow-clockwise"
              viewBox="0 0 16 16"
            >
              <path
                fillRule="evenodd"
                d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"
              />
              <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466" />
            </svg>

            <input
              type="range"
              min={0}
              max={360}
              step={1}
              value={rotation}
              onChange={handleChange}
              className="ms-2"
            />
          </div>
          {/*  <div className="image-tab-option">
          <div className="image-tab-option-content">
            <button onClick={getCroppedImage}>Crop Image</button>
          </div>
        </div> */}
        </div>
      </div>
      {type === 3 && (
        <div className="d-center">
          <ButtonSingle
            handleClick={getCroppedImage}
            width={type === 3 ? 300 : undefined}
          >
            {AppTranslation.text("send")}
          </ButtonSingle>
        </div>
      )}
    </>
  );
};

async function cropImage(imageSrc: string, crop: Area): Promise<string> {
  const image = await loadImage(imageSrc);

  const canvas = document.createElement("canvas");
  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext("2d");

  if (!ctx) throw new Error("Failed to get canvas context");

  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    crop.width,
    crop.height
  );

  return canvas.toDataURL("image/jpeg");
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = reject;
  });
}
